import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import { useDiscussionRepository } from '../../../../../../services/adaptors/discussionRepositoryAdapter'
import { Discussion } from '../../../../../../domain/entity/DiscussionEntity'
import { VersionReportVersionEntity } from '../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { intl } from '../../../../../../i18n'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../store/messages'

type UseDiscussionProps = {
  versionPageUuid?: string
  currentVersionUuid?: string
  templatePageUuid?: string
  versionList: VersionReportVersionEntity[]
}

const EMPTY_CACHE: ReadonlyMap<string, Discussion> = new Map()
const EMPTY_UUID_SET: ReadonlySet<string> = new Set()

export const useDiscussion = ({
  versionPageUuid,
  currentVersionUuid,
  templatePageUuid,
  versionList,
}: UseDiscussionProps) => {
  const [currentDiscussion, setCurrentDiscussion] = useState<Discussion | null>(
    null
  )
  const [cacheByPage, setCacheByPage] = useState<
    Map<string, Map<string, Discussion>>
  >(new Map())
  const [expandedByPage, setExpandedByPage] = useState<
    Map<string, Set<string>>
  >(new Map())
  const [loadingByPage, setLoadingByPage] = useState<Map<string, Set<string>>>(
    new Map()
  )
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false)
  const cacheByPageRef = useRef(cacheByPage)
  const expandedByPageRef = useRef(expandedByPage)
  const loadingByPageRef = useRef(loadingByPage)
  cacheByPageRef.current = cacheByPage
  expandedByPageRef.current = expandedByPage
  loadingByPageRef.current = loadingByPage
  const { getDiscussion, getDiscussionByVersion } = useDiscussionRepository()
  const dispatch = useDispatch()

  const versionCache = versionPageUuid
    ? (cacheByPage.get(versionPageUuid) ?? EMPTY_CACHE)
    : EMPTY_CACHE
  const expandedVersionUuids = versionPageUuid
    ? (expandedByPage.get(versionPageUuid) ?? EMPTY_UUID_SET)
    : EMPTY_UUID_SET
  const loadingVersionUuids = versionPageUuid
    ? (loadingByPage.get(versionPageUuid) ?? EMPTY_UUID_SET)
    : EMPTY_UUID_SET

  const pastVersions = useMemo(() => {
    if (!currentVersionUuid) return []
    const currentVer = versionList.find(v => v.uuid === currentVersionUuid)
    if (!currentVer) return []

    const currentCreatedAt = moment(currentVer.createdAt).valueOf()
    return [...versionList]
      .filter(
        v =>
          v.uuid !== currentVersionUuid &&
          moment(v.createdAt).valueOf() < currentCreatedAt
      )
      .sort(
        (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
      )
  }, [versionList, currentVersionUuid])

  const fetchCurrent = useCallback(async () => {
    if (!versionPageUuid) {
      setCurrentDiscussion(null)
      return
    }
    try {
      setIsLoadingCurrent(true)
      const result = await getDiscussion(versionPageUuid)
      setCurrentDiscussion(result)
    } catch {
      dispatch(
        addGlobalMessage({
          type: MessageLevel.ERROR,
          title: intl.formatMessage({ id: 'discussion.error.fetch' }),
        })
      )
      setCurrentDiscussion(null)
    } finally {
      setIsLoadingCurrent(false)
    }
  }, [versionPageUuid, getDiscussion, dispatch])

  useEffect(() => {
    fetchCurrent()
  }, [fetchCurrent])

  const loadVersion = useCallback(
    async (versionUuid: string) => {
      if (!templatePageUuid || !versionPageUuid) return
      const pageUuid = versionPageUuid
      if (cacheByPageRef.current.get(pageUuid)?.has(versionUuid)) return
      if (loadingByPageRef.current.get(pageUuid)?.has(versionUuid)) return
      setLoadingByPage(prev => {
        const next = new Map(prev)
        const set = new Set(next.get(pageUuid) ?? [])
        set.add(versionUuid)
        next.set(pageUuid, set)
        return next
      })
      try {
        const result = await getDiscussionByVersion(
          versionUuid,
          templatePageUuid
        )
        if (result) {
          setCacheByPage(prev => {
            const next = new Map(prev)
            const pageCache = new Map(next.get(pageUuid) ?? [])
            pageCache.set(versionUuid, result.discussion)
            next.set(pageUuid, pageCache)
            return next
          })
        }
      } catch {
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: intl.formatMessage({ id: 'discussion.error.fetch' }),
          })
        )
      } finally {
        setLoadingByPage(prev => {
          const next = new Map(prev)
          const set = new Set(next.get(pageUuid) ?? [])
          set.delete(versionUuid)
          if (set.size === 0) {
            next.delete(pageUuid)
          } else {
            next.set(pageUuid, set)
          }
          return next
        })
      }
    },
    [templatePageUuid, versionPageUuid, getDiscussionByVersion, dispatch]
  )

  const toggleVersion = useCallback(
    (versionUuid: string) => {
      if (!versionPageUuid) return
      const pageUuid = versionPageUuid
      const isOpening = !expandedByPageRef.current
        .get(pageUuid)
        ?.has(versionUuid)
      setExpandedByPage(prev => {
        const next = new Map(prev)
        const set = new Set(next.get(pageUuid) ?? [])
        if (set.has(versionUuid)) {
          set.delete(versionUuid)
        } else {
          set.add(versionUuid)
        }
        next.set(pageUuid, set)
        return next
      })

      if (isOpening) {
        loadVersion(versionUuid)
      }
    },
    [versionPageUuid, loadVersion]
  )

  return {
    currentDiscussion,
    pastVersions,
    versionCache,
    expandedVersionUuids,
    isLoadingCurrent,
    loadingVersionUuids,
    toggleVersion,
    refreshCurrent: fetchCurrent,
  }
}
